import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Plus, Search } from "lucide-react";
import { getEventTypes } from "@/features/supply-config/event-types-api";
import {
  EVENT_TYPE_FLAGS,
  getEventTypeFlagsBatch,
  type EventTypeFlagType,
} from "@/features/supply-config/feature-flags-api";
import {
  SupplyListCard,
  SupplyPageHeader,
} from "@/features/supply-config/ui/supply-list";
import { HasAccess } from "@/components/common/has-access";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

const TYPES = [
  { key: "all", label: "All" },
  { key: "individual", label: "Individual" },
  { key: "round_robin", label: "Round-robin" },
] as const;

export default function EventTypes() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [type, setType] = useState<"all" | "individual" | "round_robin">("all");
  const [page, setPage] = useState(0);
  const limit = 20;

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["event-types", q, type, page],
    queryFn: () => getEventTypes({ q, type, page, limit }),
    placeholderData: keepPreviousData,
  });
  const items = data?.eventTypes ?? [];
  const total = data?.total ?? 0;
  const lastPage = data?.lastPage ?? 0;

  // Feature flags for the page's event-type URIs (batched), so each row can show
  // whether usage-automation / auto-cancellation are set + on.
  const uris = items
    .map((e) => e.calendly_event_type_uri)
    .filter((u): u is string => !!u);
  const { data: flags = [] } = useQuery({
    queryKey: ["feature-flags", "batch", uris.join(",")],
    queryFn: () => getEventTypeFlagsBatch(uris),
    enabled: uris.length > 0,
    placeholderData: keepPreviousData,
  });
  // identifier → { [type]: enabled }
  const flagMap = flags.reduce<Record<string, Partial<Record<EventTypeFlagType, boolean>>>>(
    (acc, f) => {
      (acc[f.identifier] ??= {})[f.type] = f.enabled;
      return acc;
    },
    {},
  );

  const setFilter = (t: typeof type) => {
    setType(t);
    setPage(0);
  };

  return (
    <div className="p-6 space-y-4">
      <SupplyPageHeader
        title="Event Types"
        actions={
          <>
            <Button variant="outline" onClick={() => navigate("raw-data")}>
              Raw Calendly data
            </Button>
            <HasAccess permission="write">
              <Button className="bg-sidebar" onClick={() => navigate("new")}>
                <Plus className="h-4 w-4 mr-1" /> New Event Type
              </Button>
            </HasAccess>
          </>
        }
      />

      <SupplyListCard
        isLoading={isLoading}
        error={error}
        isEmpty={items.length === 0}
        emptyText={
          q || type !== "all"
            ? "No event types match your filters."
            : "No event types yet."
        }
        controls={
          <>
            <div className="flex items-center gap-2">
              {TYPES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setFilter(t.key)}
                  className={`px-2.5 py-1 rounded-md text-xs border ${
                    type === t.key
                      ? "bg-sidebar text-white border-sidebar"
                      : "bg-white text-gray-600 border-gray-200"
                  }`}
                >
                  {t.label}
                </button>
              ))}
              {!isLoading && !error && (
                <span className="text-xs text-gray-400 ml-1">{total}</span>
              )}
            </div>
            <div className="relative w-72 max-w-full">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <Input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(0);
                }}
                placeholder="Search professional, managed event, team, language…"
                className="h-8 pl-7 text-sm"
              />
            </div>
          </>
        }
        footer={
          <div className="flex items-center justify-end gap-2 px-4 py-2 border-t border-gray-100 bg-gray-50/50">
            <span className="text-xs text-gray-400 mr-1">
              Page {page + 1} of {lastPage + 1}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page <= 0 || isFetching}
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => p + 1)}
              disabled={data?.nextPage == null || isFetching}
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        }
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white text-left text-xs text-gray-500">
              <th className="py-2 px-4">Managed event</th>
              <th className="py-2 px-4">Mapped to</th>
              <th className="py-2 px-4">Language</th>
              <th className="py-2 px-4">Calendly URI</th>
              <th className="py-2 px-4">Automation</th>
              <th className="py-2 px-4">Active</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr
                key={e.id}
                className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(e.id)}
              >
                <td className="py-2 px-4">
                  <button
                    type="button"
                    className="font-medium text-blue-600 hover:underline text-left"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      navigate(`../managed-events/${e.managed_event_id}`);
                    }}
                  >
                    {e.managed_event?.name ?? "—"}
                  </button>
                </td>
                <td className="py-2 px-4">
                  {e.professional ? (
                    <button
                      type="button"
                      title="Filter by this professional"
                      className="hover:underline"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        setQ(e.professional!.name);
                        setPage(0);
                      }}
                    >
                      <Badge variant="outline">👤 {e.professional.name}</Badge>
                    </button>
                  ) : e.teams?.length ? (
                    <Badge variant="outline">
                      👥 {e.teams.map((t) => t.name).join(", ")}
                      {e.type ? ` · ${e.type}` : e.language ? ` · ${e.language}` : ""}
                    </Badge>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="py-2 px-4">{e.language ?? "—"}</td>
                <td className="py-2 px-4 font-mono text-[11px] text-gray-400 truncate max-w-[240px]">
                  {e.calendly_event_type_uri ?? "—"}
                </td>
                <td className="py-2 px-4">
                  {e.calendly_event_type_uri ? (
                    <div className="flex flex-wrap gap-1">
                      {EVENT_TYPE_FLAGS.map((f) => {
                        const enabled =
                          flagMap[e.calendly_event_type_uri!]?.[f.type];
                        // Short chip: on (green) / off (gray) / not set (hidden dash)
                        const short =
                          f.type === "SESSION_USAGE_AUTOMATION"
                            ? "Usage"
                            : "Cancel";
                        return enabled === undefined ? (
                          <Badge
                            key={f.type}
                            variant="outline"
                            className="text-[10px] text-gray-300"
                            title={`${f.label}: not set`}
                          >
                            {short}
                          </Badge>
                        ) : (
                          <Badge
                            key={f.type}
                            variant={enabled ? "active" : "inactive"}
                            className="text-[10px]"
                            title={`${f.label}: ${enabled ? "on" : "off"}`}
                          >
                            {short} {enabled ? "✓" : "✗"}
                          </Badge>
                        );
                      })}
                    </div>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="py-2 px-4">{e.is_active ? "Yes" : "No"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </SupplyListCard>
    </div>
  );
}
